import cc from "classcat";
import React, { useRef, useState } from "react";
import TabsList from "./TabsList";
import TabsPanel from "./TabsPanel";
import TabsTab from "./TabsTab";
import TabsContext, { TabsKind } from "./context";

const noop = () => {};

interface TabsProps {
  /**
   * Direct children of `Tabs` should be one of:
   * `Tabs.List` or `Tabs.Panel`
   */
  children: React.ReactNode;
  /** Sets _default_ tab selection by index in source order */
  defaultSelectedIndex?: number;
  /**
   * Sets selected tab by index, making Tabs **fully controlled**.
   * When using this prop, you must use the `onTabChange` callback
   * to update the value of this prop to update the selected tab.
   */
  selectedIndex?: number;
  /** Callback invoked with the index of the tab the user is moving selection to */
  onTabChange?: (index: number) => void;
  /**
   * Shows bottom border when `true`.
   * Only applies to `kind="default"`.
   * @deprecated Will be removed in a future release.
   */
  hasBorder?: boolean;
  /** Visual style variant of the tabs */
  kind?: TabsKind;
  /** Optional value for `data-testid` attribute */
  testId?: string;
}

/**
 * Component that handles tabs and tab panels based on WAI-ARIA [best practices](https://www.w3.org/TR/wai-aria-practices/#tabpanel)
 * for the "tabs" design pattern.
 *
 * The `Tabs` component manages its own state, changing the visible tab panel based
 * on user events. Use the `onTabChange` callback to add any custom behaviors.
 */
const Tabs = ({
  children,
  defaultSelectedIndex = 0,
  selectedIndex = null,
  onTabChange = noop,
  hasBorder = true,
  kind = "default",
  testId,
}: TabsProps) => {
  const tabsListRef = useRef<HTMLUListElement>();
  const [tabIds, setTabIds] = useState<string[]>([]);
  const [hasPanels, setHasPanels] = useState(false);
  const [isResponsive, setIsResponsive] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(defaultSelectedIndex);
  const isControlledComponent = selectedIndex !== null;

  const changeTabs = (tabId) => {
    const tabIndex = tabIds.indexOf(tabId);
    onTabChange(tabIndex);

    if (!isControlledComponent) {
      setCurrentIndex(tabIndex);
    }
  };

  return (
    <TabsContext.Provider
      value={{
        tabIds,
        setTabIds,
        currentIndex: isControlledComponent ? selectedIndex : currentIndex,
        hasPanels,
        setHasPanels,
        changeTabs,
        tabsListRef,
        isResponsive,
        setIsResponsive,
        kind,
      }}
    >
      <div
        className={cc([
          "nds-tabs",
          `nds-tabs--${kind}`,
          {
            "nds-tabs--isResponsive": isResponsive,
            "nds-tabs--bordered": hasBorder && kind === "default",
          },
        ])}
        data-testid={testId}
      >
        {children}
      </div>
    </TabsContext.Provider>
  );
};

Tabs.List = TabsList;
Tabs.Tab = TabsTab;
Tabs.Panel = TabsPanel;

export default Tabs;
